'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { Medal } from 'lucide-react'
import Skeleton from '@/components/elements/Skeleton'
import { useAuth } from '@/lib/commerce/hooks'
import { getLoyaltyTiersApi, getLoyaltySummaryApi } from '@/lib/commerce/services/loyalty-api'
import { navigateUrl } from '@/utils/client-request'
import { formatImageURL, formatNumber, formatPrice, isCustomerAuthActive } from '@/utils/util'
import { STATUS_LABELS } from '@/utils/order'
import { panelFocusRing } from '../elements/SidePanel'
import ConvertVoucherModal from './ConvertVoucherModal'

const eyebrow = 'text-[11px] tracking-[0.14em] uppercase text-muted-foreground'
const solidButton = `inline-flex items-center justify-center h-11 px-8 bg-foreground text-background text-[13px] tracking-wide transition-opacity duration-150 hover:opacity-85 disabled:opacity-40 disabled:cursor-not-allowed ${panelFocusRing}`
const outlinedButton = `inline-flex items-center justify-center h-11 px-8 border border-foreground text-foreground text-[13px] tracking-wide transition-colors duration-150 hover:bg-foreground hover:text-background ${panelFocusRing}`
const sweepLink = `text-[13px] pb-0.5 bg-[linear-gradient(currentColor,currentColor)] bg-no-repeat bg-position-[0_100%] bg-size-[100%_1px] hover:bg-size-[0%_1px] transition-[background-size] duration-500 ease-out motion-reduce:transition-none ${panelFocusRing}`

function TierIcon({ icon }: { icon?: string }) {
  return icon
    ? <img src={formatImageURL(icon)} alt="" className="size-10 object-cover bg-muted" />
    : <div className="size-10 bg-muted grid place-items-center"><Medal aria-hidden className="size-4 text-muted-foreground" strokeWidth={1.25} /></div>
}

export default function Loyalty({ vendor }: { vendor: any }) {
  const pathname = usePathname()
  const { session, isLoggedIn } = useAuth()

  const [program, setProgram] = useState<any>(null)
  const [loadingProgram, setLoadingProgram] = useState(true)
  const [summary, setSummary] = useState<any>(null)
  const [loadedKey, setLoadedKey] = useState(-1)
  const [convertOpen, setConvertOpen] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const tiers = program?.tiers || []
  const loadingSummary = isLoggedIn && loadedKey !== refreshKey
  const ctx = { vendorId: vendor?.id, session: session || undefined }

  useEffect(() => {
    let cancelled = false
    getLoyaltyTiersApi({ vendorId: vendor?.id }).then((programRes: any) => {
      if (cancelled) return
      setProgram(programRes?.data || null)
    }).catch(() => { }).finally(() => { if (!cancelled) setLoadingProgram(false) })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!isLoggedIn) return
    let cancelled = false
    getLoyaltySummaryApi(ctx).then((summaryRes) => {
      if (cancelled) return
      setSummary(summaryRes?.data || null)
    }).catch(() => { }).finally(() => { if (!cancelled) setLoadedKey(refreshKey) })
    return () => { cancelled = true }
  }, [isLoggedIn, refreshKey])

  const benefitLabel = (benefit: any) => {
    if (benefit.type === 'percentDiscount') return `${benefit.valuePercent}% хөнгөлөлт`
    if (benefit.type === 'fixedDiscount') return `${formatPrice(benefit.valueAmount)} хөнгөлөлт`
    if (benefit.type === 'freeShipping') return 'Үнэгүй хүргэлт'
    if (benefit.type === 'shippingDiscount') return `Хүргэлт ${benefit.valuePercent}% хөнгөлөлттэй`
    return 'Нэмэлт урамшуулал'
  }

  const progressPercent = summary?.nextTier
    ? Math.min(100, Math.round((summary.qualifyingSpend / summary.nextTier.minAnnualSpend) * 100))
    : 100

  const steps = [
    { t: 'Худалдан авалт бүрээс оноо цуглуул', s: `Захиалга "${STATUS_LABELS[program?.awardStatus] || STATUS_LABELS.paid}" төлөвт орох бүрд түвшнийхээ хувиар оноо орно.` },
    { t: 'Түвшин ахиж, илүү урамшуулал ав', s: 'Жилийн худалдан авалтаараа дээд түвшинд хүрч, илүү хөнгөлөлт эдлээрэй.' },
    { t: 'Оноогоо ваучер болгон ашигла', s: 'Цуглуулсан оноогоо ваучер болгон дараагийн захиалгадаа хэрэглэнэ.' },
  ]

  const tierGrid = tiers.length >= 3 ? 'md:grid-cols-3' : tiers.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-1 max-w-md'

  return (
    <div className="w-full px-4 lg:px-6 pt-8 lg:pt-12 pb-20">
      <nav aria-label="Breadcrumb" className="flex items-center gap-x-1.5 text-xs text-muted-foreground">
        <a href={navigateUrl('/', pathname)} className={`transition-colors duration-150 hover:text-foreground ${panelFocusRing}`}>Нүүр</a>
        <span aria-hidden>/</span>
      </nav>

      <header className="mt-8 lg:mt-16 max-w-3xl">
        <div className={eyebrow}>Лоялти хөтөлбөр</div>
        <h1 className="mt-4 text-4xl sm:text-6xl tracking-tight text-foreground">Оноо цуглуулж, түвшнээ ахиулаарай</h1>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">Худалдан авалт бүрээсээ оноо цуглуулж, түвшин ахих тусам илүү их хөнгөлөлт аваарай. Цуглуулсан оноогоо ваучер болгон дараагийн захиалгадаа ашиглах боломжтой.</p>
      </header>

      {!isLoggedIn && isCustomerAuthActive(vendor) && (
        loadingProgram ? (
          <Skeleton className="mt-12 lg:mt-16 h-32 w-full rounded-none" />
        ) : (
          <section className="mt-12 lg:mt-16 py-8 border-y border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div>
              {program?.welcomeBonusPoints > 0 ? (
                <>
                  <div className="text-3xl sm:text-4xl font-light tracking-tight tabular-nums">+{formatNumber(program.welcomeBonusPoints)} оноо</div>
                  <p className="mt-2 text-sm text-muted-foreground">Бүртгэлээ үүсгэж үнэгүй оноо аваад, ваучер болгон ашиглаарай.</p>
                </>
              ) : (
                <>
                  <div className="text-2xl sm:text-3xl tracking-tight">Оноо цуглуулж эхлээрэй</div>
                  <p className="mt-2 text-sm text-muted-foreground">Бүртгүүлсэн худалдан авагч бүр худалдан авалтаасаа оноо цуглуулна.</p>
                </>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <a href={navigateUrl('/register?url=/loyalty', pathname)} className={solidButton}>Бүртгүүлэх</a>
              <a href={navigateUrl('/login?url=/loyalty', pathname)} className={outlinedButton}>Нэвтрэх</a>
            </div>
          </section>
        )
      )}

      {isLoggedIn && (
        loadingSummary && !summary ? (
          <Skeleton className="mt-12 lg:mt-16 h-40 w-full rounded-none" />
        ) : summary ? (
          <section className="mt-12 lg:mt-16 py-8 border-y border-border">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
              <div className="flex flex-wrap items-end gap-x-12 gap-y-6">
                <div>
                  <div className={eyebrow}>Таны түвшин</div>
                  <div className="mt-3 flex items-center gap-3">
                    <TierIcon icon={summary.currentTier?.icon} />
                    <span className="text-xl">{summary.currentTier?.name || '—'}</span>
                  </div>
                </div>
                <div>
                  <div className={eyebrow}>Онооны үлдэгдэл</div>
                  <div className="mt-2 text-4xl font-light tracking-tight tabular-nums">{formatNumber(summary.balance || 0)}</div>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <a href={navigateUrl('/points', pathname)} className={sweepLink}>Онооны түүх</a>
                <button type="button" onClick={() => setConvertOpen(true)} disabled={(summary.balance || 0) < (program?.minPointsToConvert || 1)} className={solidButton}>Ваучер болгох</button>
              </div>
            </div>
            <div className="mt-8">
              {summary.nextTier ? (
                <>
                  <div className="flex flex-wrap items-baseline justify-between gap-2 text-[13px]">
                    <span className="text-muted-foreground">Энэ жилийн худалдан авалт: <span className="text-foreground tabular-nums">{formatPrice(summary.qualifyingSpend || 0)}</span></span>
                    <span className="text-muted-foreground tabular-nums">{summary.nextTier.name}: {formatPrice(summary.nextTier.minAnnualSpend)}</span>
                  </div>
                  <div className="mt-3 h-0.5 bg-border" role="progressbar" aria-valuenow={progressPercent} aria-valuemin={0} aria-valuemax={100} aria-label={`${summary.nextTier.name} түвшин хүртэлх явц`}>
                    <div className="h-full bg-foreground transition-[width] duration-500" style={{ width: `${progressPercent}%` }} />
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground tabular-nums">{summary.nextTier.name} түвшинд хүрэхэд {formatPrice(summary.nextTier.remainingSpend)} дутуу байна</p>
                </>
              ) : (
                <p className="text-[13px] text-muted-foreground">Та хамгийн дээд түвшинд байна. Энэ жилийн худалдан авалт <span className="text-foreground tabular-nums">{formatPrice(summary.qualifyingSpend || 0)}</span></p>
              )}
            </div>
          </section>
        ) : null
      )}

      <section className="mt-16 lg:mt-24">
        <h2 className="text-2xl sm:text-4xl tracking-tight">Хэрхэн ажилладаг вэ</h2>
        <ol className="mt-8 lg:mt-10 grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-8">
          {steps.map((step, index) => (
            <li key={index} className="pt-6 border-t border-foreground">
              <div className="text-xs tabular-nums text-muted-foreground">{String(index + 1).padStart(2, '0')}</div>
              <div className="mt-3 text-base text-foreground">{step.t}</div>
              <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">{step.s}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="mt-16 lg:mt-24">
        <h2 className="text-2xl sm:text-4xl tracking-tight">Түвшингүүд</h2>
        {loadingProgram ? (
          <div className="mt-8 lg:mt-10 grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-10">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="pt-6 border-t border-border space-y-3">
                <Skeleton className="size-10 rounded-none" />
                <Skeleton className="h-6 w-28 rounded-none" />
                <Skeleton className="h-4 w-40 rounded-none" />
                <Skeleton className="h-4 w-32 rounded-none" />
              </div>
            ))}
          </div>
        ) : (
          <div className={`mt-8 lg:mt-10 grid grid-cols-1 ${tierGrid} gap-x-10 gap-y-10`}>
            {tiers.map((tier: any) => {
              const isCurrent = summary?.currentTier?.id === tier.id
              return (
                <article key={tier.id} className={`pt-6 border-t ${isCurrent ? 'border-t-2 border-foreground' : 'border-border'}`}>
                  <div className="flex items-start justify-between gap-4">
                    <TierIcon icon={tier.icon} />
                    {isCurrent && <span className="text-[11px] tracking-[0.14em] uppercase text-foreground">Таны түвшин</span>}
                  </div>
                  <h3 className="mt-5 text-2xl tracking-tight">{tier.name}</h3>
                  <div className="mt-1 text-[13px] text-muted-foreground tabular-nums">{tier.minAnnualSpend > 0 ? `${formatPrice(tier.minAnnualSpend)}-с дээш` : 'Эхлэх түвшин'}</div>
                  <ul className="mt-6 space-y-2.5 text-sm">
                    <li className="flex gap-3"><span aria-hidden className="text-muted-foreground">—</span><span>{formatNumber(tier.spendPerPoint)}₮ тутамд {formatNumber(tier.pointsPerUnit)} оноо</span></li>
                    {tier.reviewRewardPoints > 0 && (
                      <li className="flex gap-3"><span aria-hidden className="text-muted-foreground">—</span><span>Сэтгэгдэл бүрд {formatNumber(tier.reviewRewardPoints)} оноо</span></li>
                    )}
                    {(tier.benefits || []).map((benefit: any) => (
                      <li key={benefit.id} className="flex gap-3"><span aria-hidden className="text-muted-foreground">—</span><span>{benefitLabel(benefit)}</span></li>
                    ))}
                  </ul>
                </article>
              )
            })}
          </div>
        )}
        <p className="mt-10 text-xs text-muted-foreground">Түвшин жилийн худалдан авалтаар тодорхойлогдож, жил бүр шинээр эхэлнэ. Цуглуулсан оноо хадгалагдана.</p>
      </section>

      <ConvertVoucherModal open={convertOpen} setOpen={setConvertOpen} vendor={vendor} program={program} balance={summary?.balance || 0} onConverted={() => setRefreshKey((key) => key + 1)} />
    </div>
  )
}
